import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  downloadStyledExemptionDayReport,
  extractExemptionEmployeesFromWorkbook,
  generateExemptionDayReportFromWorkbook,
  parseDayList,
  REPORT_CODE_DESCRIPTIONS
} from "../helpers/exemptionDayReport";
import {
  EXEMPTION_EXCLUDED_VALUE,
  EXEMPTION_UNASSIGNED_VALUE,
  getAssignmentTemplate,
  saveAssignmentTemplate
} from "../services/exemptionAssignmentService";

const inputStyle = (isDark) => ({
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: `1px solid ${isDark ? "#2c3d55" : "#dbe2ea"}`,
  background: isDark ? "#111827" : "#fff",
  color: isDark ? "#e5edf6" : "#111827",
  fontWeight: 700,
  boxSizing: "border-box",
  outline: "none"
});

const cardStyle = (isDark) => ({
  background: isDark ? "#172234" : "#fff",
  border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`,
  borderRadius: 18,
  padding: 20,
  boxShadow: isDark ? "0 16px 36px rgba(0,0,0,0.20)" : "0 12px 30px rgba(15,23,42,0.06)"
});

const labelStyle = (isDark) => ({
  display: "block",
  marginBottom: 8,
  color: isDark ? "#cbd5e1" : "#334155",
  fontSize: 13,
  fontWeight: 900
});

const parseExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, {
          type: "array",
          cellDates: false,
          cellStyles: true,
          raw: false
        });

        resolve(workbook);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Excel dosyası okunamadı."));
    reader.readAsArrayBuffer(file);
  });

const getPreviousMonthKey = () => {
  const now = new Date();
  const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  return `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, "0")}`;
};

const getProjectCompanyName = (project = {}) =>
  project.firmaKisaAdi || project.firmaAdi || "Firma";

const getProjectCompanyKey = (project = {}) =>
  String(getProjectCompanyName(project) || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, " ");

const parseProjectDateValue = (value) => {
  if (value?.toDate) {
    return value.toDate();
  }

  const text = String(value || "").trim();
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const turkishMatch = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (turkishMatch) {
    return new Date(
      Number(turkishMatch[3]),
      Number(turkishMatch[2]) - 1,
      Number(turkishMatch[1])
    );
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isActiveStbProject = (project = {}) => {
  const stbCode = String(project.projeStbKodu || "").trim();

  // Gerçek STB proje kodları sayısaldır; DB ve serbest metin kodları rapora alınmaz.
  if (!/^\d+$/.test(stbCode)) return false;
  if (project.isArchived === true) return false;
  if (String(project.sonlandirmaTarihi || "").trim()) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = parseProjectDateValue(project.baslangicTarihi);
  if (startDate) {
    startDate.setHours(0, 0, 0, 0);
    if (startDate > today) return false;
  }

  const endDates = [project.bitisTarihi, project.ekSureTarihi]
    .map(parseProjectDateValue)
    .filter(Boolean)
    .map((date) => {
      date.setHours(0, 0, 0, 0);
      return date;
    });
  const effectiveEndDate = endDates.length
    ? new Date(Math.max(...endDates.map((date) => date.getTime())))
    : null;

  return !effectiveEndDate || effectiveEndDate >= today;
};

export default function ExemptionDayReportView({ projects = [], themeMode = "light" }) {
  const isDark = themeMode === "dark";
  const [periodKey, setPeriodKey] = useState(getPreviousMonthKey);
  const [projectCode, setProjectCode] = useState("");
  const [officialHolidayText, setOfficialHolidayText] = useState("");
  const [bayramHolidayText, setBayramHolidayText] = useState("");
  const [specialCodeText, setSpecialCodeText] = useState("");
  const [extraPersonNotes, setExtraPersonNotes] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [sourceWorkbook, setSourceWorkbook] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState("");
  const [employeeAssignments, setEmployeeAssignments] = useState({});
  const [templateMessage, setTemplateMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const activeProjects = useMemo(
    () => projects.filter(isActiveStbProject),
    [projects]
  );

  const companyOptions = useMemo(() => {
    const map = new Map();

    activeProjects.forEach((project) => {
      const key = getProjectCompanyKey(project);

      if (!key) return;

      const existing = map.get(key) || {
        key,
        name: getProjectCompanyName(project),
        projects: []
      };

      existing.projects.push(project);
      map.set(key, existing);
    });

    return Array.from(map.values())
      .map((company) => ({
        ...company,
        projects: company.projects.sort((a, b) =>
          String(a.projeStbKodu || "").localeCompare(String(b.projeStbKodu || ""), "tr")
        )
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [activeProjects]);

  const selectedCompany = useMemo(() =>
    companyOptions.find((company) => company.key === selectedCompanyKey) || null,
  [companyOptions, selectedCompanyKey]);

  const selectedCompanyProjects = selectedCompany?.projects || [];
  const needsAssignment = Boolean(sourceWorkbook && selectedCompany && selectedCompanyProjects.length > 1);

  const unassignedEmployees = useMemo(() => {
    if (!needsAssignment) return [];

    return employeeList.filter((employee) =>
      !String(employeeAssignments[employee.key] || "").trim()
    );
  }, [employeeAssignments, employeeList, needsAssignment]);

  const projectCodeOptions = useMemo(() => {
    const map = new Map();

    activeProjects.forEach((project) => {
      const code = String(project.projeStbKodu || "").trim();

      if (!code) return;

      map.set(code, {
        code,
        projectName: project.projeAdi || "",
        label: `${code} — ${project.firmaKisaAdi || project.firmaAdi || "Firma"} / ${project.projeAdi || "Proje"}`
      });
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [activeProjects]);

  const selectedProjectOption = useMemo(() => {
    const cleanCode = String(projectCode || "").trim();

    if (!cleanCode) return null;

    return projectCodeOptions.find((option) => option.code === cleanCode) || null;
  }, [projectCode, projectCodeOptions]);

  useEffect(() => {
    let isMounted = true;

    const loadTemplate = async () => {
      if (!selectedCompanyKey) {
        setEmployeeAssignments({});
        setTemplateMessage("");
        return;
      }

      setIsTemplateLoading(true);
      setTemplateMessage("");

      try {
        const template = await getAssignmentTemplate(selectedCompanyKey);

        if (!isMounted) return;

        setEmployeeAssignments(template?.assignments || {});
        setTemplateMessage(template ? "Kayıtlı dağılım şablonu uygulandı." : "Bu firma için kayıtlı dağılım şablonu yok.");
      } catch (error) {
        if (!isMounted) return;

        setEmployeeAssignments({});
        setTemplateMessage(`Şablon yüklenemedi: ${error.message}`);
      } finally {
        if (isMounted) {
          setIsTemplateLoading(false);
        }
      }
    };

    loadTemplate();

    return () => {
      isMounted = false;
    };
  }, [selectedCompanyKey]);

  const updateEmployeeAssignment = (employeeKey, value) => {
    setEmployeeAssignments((current) => ({
      ...current,
      [employeeKey]: value
    }));
  };

  const saveCurrentAssignments = async () => {
    if (!selectedCompany) {
      setErrorMessage("Şablon kaydetmek için firma seçin.");
      return;
    }

    setIsTemplateSaving(true);
    setErrorMessage("");
    setTemplateMessage("");

    try {
      const nextAssignments = employeeList.reduce((acc, employee) => {
        const value = employeeAssignments[employee.key];

        if (value) {
          acc[employee.key] = value;
        }

        return acc;
      }, {});

      await saveAssignmentTemplate({
        companyKey: selectedCompany.key,
        companyName: selectedCompany.name,
        assignments: nextAssignments
      });
      setEmployeeAssignments(nextAssignments);
      setTemplateMessage("Dağılım şablonu kaydedildi.");
    } catch (error) {
      setTemplateMessage(`Şablon kaydedilemedi: ${error.message}`);
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const handleSelectedFile = async (file) => {
    if (!file) return;

    setErrorMessage("");
    setGeneratedReport(null);
    setSelectedFileName(file.name);

    try {
      const workbook = await parseExcelFile(file);
      const employees = extractExemptionEmployeesFromWorkbook(workbook);

      setSourceWorkbook(workbook);
      setEmployeeList(employees);

      if (employees.length === 0) {
        setErrorMessage("Excel yüklendi ancak Personel başlığından sonra okunabilir personel satırı bulunamadı.");
      }
    } catch (error) {
      setSourceWorkbook(null);
      setEmployeeList([]);
      setErrorMessage(`Excel okunamadı: ${error.message}`);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    try {
      await handleSelectedFile(file);
    } finally {
      event.target.value = "";
    }
  };

  const handleFileDrop = async (event) => {
    event.preventDefault();
    setIsDraggingFile(false);

    const file = event.dataTransfer.files?.[0];
    await handleSelectedFile(file);
  };

  const generateReport = async () => {
    if (!sourceWorkbook) {
      setErrorMessage("Önce portal sisteminden indirilen dönem çalışma Excel dosyasını yükleyin.");
      return;
    }

    if (!periodKey) {
      setErrorMessage("Dönem seçimi zorunludur.");
      return;
    }

    if (employeeList.length === 0) {
      setErrorMessage("Excel içinde hesaplanacak personel bulunamadı. Personel başlığı ve altındaki satırları kontrol edin.");
      return;
    }

    if (needsAssignment && unassignedEmployees.length > 0) {
      setErrorMessage(`${unassignedEmployees.length} personel için proje veya raporlu/dışarıda seçimi yapılmalı.`);
      return;
    }

    const assignmentProjectCodes = needsAssignment
      ? employeeList.reduce((acc, employee) => {
          const value = String(employeeAssignments[employee.key] || "").trim();

          if (value && value !== EXEMPTION_EXCLUDED_VALUE) {
            acc[employee.key] = value;
          }

          return acc;
        }, {})
      : null;
    const includedEmployeeKeys = assignmentProjectCodes
      ? Object.keys(assignmentProjectCodes)
      : null;
    const reportProjectCode = needsAssignment ? "" : projectCode;
    const reportProjectName = needsAssignment ? "" : (selectedProjectOption?.projectName || "");
    const activeProjectNotes = needsAssignment
      ? selectedCompanyProjects
          .map((project) => {
            const code = String(project.projeStbKodu || "").trim();
            const name = String(project.projeAdi || "").trim();

            if (!code && !name) return "";

            return name ? `${code}: ${name}` : `${code}: Proje adı`;
          })
          .filter(Boolean)
      : [];

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const report = generateExemptionDayReportFromWorkbook({
        workbook: sourceWorkbook,
        periodKey,
        projectCode: reportProjectCode,
        projectName: reportProjectName,
        officialHolidayDays: parseDayList(officialHolidayText),
        bayramHolidayDays: parseDayList(bayramHolidayText),
        specialCodeText,
        extraPersonNotes,
        includedEmployeeKeys,
        assignmentProjectCodes,
        activeProjectNotes
      });

      setGeneratedReport(report);
    } catch (error) {
      setGeneratedReport(null);
      setErrorMessage(`Rapor oluşturulamadı: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadReport = async () => {
    if (!generatedReport?.workbook) {
      setErrorMessage("İndirilecek rapor bulunamadı. Önce raporu oluşturun.");
      return;
    }

    setIsDownloading(true);
    setErrorMessage("");

    try {
      await downloadStyledExemptionDayReport(generatedReport);
    } catch (error) {
      setErrorMessage(`Excel indirilemedi: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const resetReportForm = () => {
    setProjectCode("");
    setOfficialHolidayText("");
    setBayramHolidayText("");
    setSpecialCodeText("");
    setExtraPersonNotes("");
    setSelectedFileName("");
    setSourceWorkbook(null);
    setGeneratedReport(null);
    setEmployeeList([]);
    setSelectedCompanyKey("");
    setEmployeeAssignments({});
    setTemplateMessage("");
    setErrorMessage("");
    setIsDraggingFile(false);
  };

  const summaryTotals = useMemo(() => {
    if (!generatedReport?.rows) {
      return {
        employees: 0,
        gun5746: 0,
        gun5510: 0,
        reportDays: 0,
        izinDays: 0
      };
    }

    return generatedReport.rows.reduce((acc, row) => ({
      employees: acc.employees + 1,
      gun5746: acc.gun5746 + Number(row.totals.gun5746 || 0),
      gun5510: acc.gun5510 + Number(row.totals.gun5510 || 0),
      reportDays: acc.reportDays + Number(row.totals.reportDays || 0),
      izinDays: acc.izinDays + Number(row.totals.oneYearMissingLeaveDays || 0)
    }), {
      employees: 0,
      gun5746: 0,
      gun5510: 0,
      reportDays: 0,
      izinDays: 0
    });
  }, [generatedReport]);

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: 0, color: isDark ? "#e5edf6" : "#124254" }}>
          Muafiyetli Gün Sayısı Raporu
        </h2>
        <p style={{ margin: "6px 0 0", color: isDark ? "#94a3b8" : "#64748b", fontWeight: 700 }}>
          Portal dönem çalışma Excel&apos;ini yükleyip 5746 / 5510 / rapor günlerini otomatik hesaplayın.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 18, alignItems: "start" }}>
        <div style={cardStyle(isDark)}>
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={labelStyle(isDark)}>Dönem</label>
              <input
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                style={inputStyle(isDark)}
              />
            </div>

            <div>
              <label style={labelStyle(isDark)}>Firma Dağılımı</label>
              <select
                value={selectedCompanyKey}
                onChange={(event) => setSelectedCompanyKey(event.target.value)}
                style={inputStyle(isDark)}
              >
                <option value="">Firma seçmeden devam et</option>
                {companyOptions.map((company) => (
                  <option key={company.key} value={company.key}>
                    {company.name} ({company.projects.length} proje)
                  </option>
                ))}
              </select>
              {isTemplateLoading && (
                <div style={{ marginTop: 6, color: isDark ? "#93c5fd" : "#2563eb", fontWeight: 800, fontSize: 12 }}>
                  Dağılım şablonu yükleniyor...
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle(isDark)}>Proje Kodu</label>
              <select
                value={projectCode}
                onChange={(event) => setProjectCode(event.target.value)}
                style={inputStyle(isDark)}
              >
                <option value="">Proje seçin</option>
                {(selectedCompanyProjects.length > 0
                  ? selectedCompanyProjects.map((project) => ({
                      code: String(project.projeStbKodu || "").trim(),
                      label: `${project.projeStbKodu} — ${project.projeAdi || "Proje"}`
                    }))
                  : projectCodeOptions
                ).map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedProjectOption?.projectName && (
                <div style={{ marginTop: 6, color: isDark ? "#93c5fd" : "#2563eb", fontWeight: 800, fontSize: 12 }}>
                  {projectCode}:{selectedProjectOption.projectName}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={labelStyle(isDark)}>Resmi Tatil Günleri</label>
              <input
                value={officialHolidayText}
                onChange={(event) => setOfficialHolidayText(event.target.value)}
                placeholder="Örn: 1, 19"
                style={inputStyle(isDark)}
              />
            </div>

            <div>
              <label style={labelStyle(isDark)}>Bayram Tatili Günleri</label>
              <input
                value={bayramHolidayText}
                onChange={(event) => setBayramHolidayText(event.target.value)}
                placeholder="Örn: 27, 28, 29, 30"
                style={inputStyle(isDark)}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle(isDark)}>Özel Kodlar</label>
            <textarea
              value={specialCodeText}
              onChange={(event) => setSpecialCodeText(event.target.value)}
              placeholder={"Her satıra: Personel;Gün veya gün aralığı;Kod\nÖrn: BEGÜM YARCI;15;R\nÖrn: EMİRCAN YIKICI;1-20;R\nÖrn: AYŞE YILMAZ;5, 8, 12;İ\nÖrn: MEHMET KAYA;18;YG"}
              rows={6}
              style={{
                ...inputStyle(isDark),
                resize: "vertical",
                lineHeight: 1.5,
                fontFamily: "inherit"
              }}
            />
          </div>

          {needsAssignment && (
            <div style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 14,
              background: isDark ? "#101827" : "#f8fafc",
              border: `1px solid ${unassignedEmployees.length > 0 ? "#f59e0b" : (isDark ? "#26364d" : "#e5e7eb")}`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ color: isDark ? "#e5edf6" : "#124254", fontWeight: 950 }}>
                    Personel Proje Dağılımı
                  </div>
                  <div style={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800, fontSize: 12 }}>
                    {employeeList.length} personel · {selectedCompanyProjects.length} aktif proje · {unassignedEmployees.length} atanmamış
                  </div>
                </div>
                <button
                  onClick={saveCurrentAssignments}
                  disabled={isTemplateSaving || employeeList.length === 0}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #0f766e",
                    background: "#0f766e",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: isTemplateSaving ? "wait" : "pointer",
                    opacity: isTemplateSaving ? 0.7 : 1
                  }}
                >
                  {isTemplateSaving ? "Kaydediliyor..." : "Dağılımı Şablona Kaydet"}
                </button>
              </div>

              {templateMessage && (
                <div style={{
                  marginBottom: 10,
                  color: templateMessage.includes("yüklenemedi") || templateMessage.includes("kaydedilemedi")
                    ? "#b91c1c"
                    : (isDark ? "#93c5fd" : "#2563eb"),
                  fontWeight: 800,
                  fontSize: 12
                }}>
                  {templateMessage}
                </div>
              )}

              {unassignedEmployees.length > 0 && (
                <div style={{
                  marginBottom: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: isDark ? "#3a2a12" : "#fffbeb",
                  border: `1px solid ${isDark ? "#854d0e" : "#fcd34d"}`,
                  color: isDark ? "#facc15" : "#92400e",
                  fontWeight: 900,
                  fontSize: 13
                }}>
                  Yeni/atanmamış personel var. Rapor oluşturmak için her personel için proje veya “Raporlu/Dışarıda” seçin.
                </div>
              )}

              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 220px",
                gap: 8,
                maxHeight: 360,
                overflow: "auto",
                paddingRight: 4
              }}>
                {employeeList.map((employee) => {
                  const assignmentValue = employeeAssignments[employee.key] || EXEMPTION_UNASSIGNED_VALUE;
                  const isUnassigned = !assignmentValue;

                  return (
                    <div
                      key={employee.key}
                      style={{
                        display: "contents"
                      }}
                    >
                      <div style={{
                        padding: "9px 10px",
                        borderRadius: 10,
                        background: isUnassigned ? (isDark ? "#2a1d12" : "#fff7ed") : (isDark ? "#172234" : "#fff"),
                        color: isDark ? "#e5edf6" : "#334155",
                        fontWeight: 850,
                        fontSize: 13,
                        border: `1px solid ${isUnassigned ? "#f59e0b" : (isDark ? "#26364d" : "#e5e7eb")}`
                      }}>
                        {employee.personel}
                      </div>
                      <select
                        value={assignmentValue}
                        onChange={(event) => updateEmployeeAssignment(employee.key, event.target.value)}
                        style={{
                          ...inputStyle(isDark),
                          padding: "9px 10px",
                          borderColor: isUnassigned ? "#f59e0b" : (isDark ? "#2c3d55" : "#dbe2ea")
                        }}
                      >
                        <option value={EXEMPTION_UNASSIGNED_VALUE}>Atanmamış</option>
                        {selectedCompanyProjects.map((project) => (
                          <option key={project.id || project.projeStbKodu} value={String(project.projeStbKodu || "").trim()}>
                            {project.projeStbKodu} · {project.projeAdi || "Proje"}
                          </option>
                        ))}
                        <option value={EXEMPTION_EXCLUDED_VALUE}>Raporlu / Dışarıda</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle(isDark)}>Excel Alt Notları</label>
            <textarea
              value={extraPersonNotes}
              onChange={(event) => setExtraPersonNotes(event.target.value)}
              placeholder={"Proje adının altına eklenecek ek personel bilgileri.\nÖrn: Ahmet Yılmaz - 01-30 arası raporlu\nÖrn: Ayşe Demir - ayın tamamı ücretsiz izin"}
              rows={4}
              style={{
                ...inputStyle(isDark),
                resize: "vertical",
                lineHeight: 1.5,
                fontFamily: "inherit"
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center"
            }}
          >
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={handleFileDrop}
              style={{
                padding: "14px 18px",
                borderRadius: 12,
                background: isDraggingFile
                  ? (isDark ? "#164e63" : "#e0f2fe")
                  : (isDark ? "#123423" : "#ecfdf5"),
                border: `1px dashed ${isDraggingFile ? "#0284c7" : (isDark ? "#276749" : "#86efac")}`,
                color: isDark ? "#86efac" : "#166534",
                fontWeight: 900,
                cursor: "pointer",
                minWidth: 230,
                textAlign: "center"
              }}
            >
              Excel Yükle veya Sürükle Bırak
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: "none" }} />
            </label>

            <button
              onClick={generateReport}
              disabled={isProcessing}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "1px solid #124254",
                background: "#124254",
                color: "#fff",
                fontWeight: 900,
                cursor: isProcessing ? "wait" : "pointer",
                opacity: isProcessing ? 0.7 : 1
              }}
            >
              {isProcessing ? "Hesaplanıyor..." : "Raporu Hesapla"}
            </button>

            <button
              onClick={downloadReport}
              disabled={!generatedReport || isDownloading}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${generatedReport && !isDownloading ? "#2563eb" : "#cbd5e1"}`,
                background: generatedReport && !isDownloading ? "#2563eb" : (isDark ? "#1e293b" : "#f8fafc"),
                color: generatedReport && !isDownloading ? "#fff" : (isDark ? "#64748b" : "#94a3b8"),
                fontWeight: 900,
                cursor: generatedReport && !isDownloading ? "pointer" : "not-allowed"
              }}
            >
              {isDownloading ? "Excel hazırlanıyor..." : "Excel İndir"}
            </button>

            <button
              onClick={resetReportForm}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${isDark ? "#475569" : "#cbd5e1"}`,
                background: isDark ? "#1e293b" : "#f8fafc",
                color: isDark ? "#e5edf6" : "#334155",
                fontWeight: 900,
                cursor: "pointer"
              }}
            >
              Yeni Hesaplama
            </button>

            {selectedFileName && (
              <span style={{ color: isDark ? "#cbd5e1" : "#475569", fontWeight: 800, fontSize: 13 }}>
                Yüklendi: {selectedFileName}
              </span>
            )}
          </div>

          {errorMessage && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 14px",
                borderRadius: 12,
                background: isDark ? "#3a1618" : "#fee2e2",
                border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"}`,
                color: isDark ? "#fca5a5" : "#b91c1c",
                fontWeight: 800
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>

        <div style={cardStyle(isDark)}>
          <h3 style={{ marginTop: 0, color: isDark ? "#e5edf6" : "#124254" }}>
            Kodlar ve Hesap Özeti
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
            {[
              ["Personel", summaryTotals.employees],
              ["5746 Toplam", summaryTotals.gun5746],
              ["5510 Toplam", summaryTotals.gun5510],
              ["Rapor", summaryTotals.reportDays]
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "#101827" : "#f8fafc",
                  border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`
                }}
              >
                <div style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 12, fontWeight: 900 }}>{label}</div>
                <div style={{ color: isDark ? "#e5edf6" : "#111827", fontSize: 22, fontWeight: 950 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {REPORT_CODE_DESCRIPTIONS.map(([code, description]) => (
              <div
                key={code}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: isDark ? "#101827" : "#f8fafc",
                  color: isDark ? "#cbd5e1" : "#334155",
                  fontWeight: 800,
                  fontSize: 13
                }}
              >
                <span>{description}</span>
                <span style={{ color: isDark ? "#93c5fd" : "#075985" }}>{code}</span>
              </div>
            ))}
          </div>

          {generatedReport?.warnings?.length > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                background: isDark ? "#2a1d12" : "#fff7ed",
                border: `1px solid ${isDark ? "#7c4a18" : "#fed7aa"}`,
                color: isDark ? "#fdba74" : "#9a3412",
                fontWeight: 800,
                fontSize: 13
              }}
            >
              {generatedReport.warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {generatedReport?.rows?.length > 0 && (
        <div style={{ ...cardStyle(isDark), marginTop: 18 }}>
          <h3 style={{ marginTop: 0, color: isDark ? "#e5edf6" : "#124254" }}>
            Önizleme
          </h3>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  {["Sıra", "Personel", "5746", "5510", "Rapor", "İ", "Ham 5746"].map((header) => (
                    <th
                      key={header}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`,
                        color: isDark ? "#cbd5e1" : "#334155",
                        fontSize: 12
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {generatedReport.rows.slice(0, 12).map((row, index) => (
                  <tr key={`${row.tcKimlikNo}-${row.personel}`}>
                    <td style={previewCellStyle(isDark)}>{index + 1}</td>
                    <td style={previewCellStyle(isDark)}>{row.personel}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.gun5746}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.gun5510 || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.reportDays || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.oneYearMissingLeaveDays || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.raw5746}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const previewCellStyle = (isDark) => ({
  padding: "10px 12px",
  borderBottom: `1px solid ${isDark ? "#26364d" : "#eef2f7"}`,
  color: isDark ? "#e5edf6" : "#111827",
  fontWeight: 700,
  fontSize: 13
});
